import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, Home } from 'lucide-react';

interface BreadcrumbItem {
  label: string;
  path?: string; // undefined = current page (no link)
}

interface BreadcrumbsProps {
  items?: BreadcrumbItem[]; // Manual override
  className?: string;
}

const ROUTE_LABELS: Record<string, string> = {
  '/': 'Home',
  '/leads': 'Lead Pipeline',
  '/playbook': 'Playbook',
  '/zandi-map': 'Submarkets',
  '/analytics': 'Analytics',
  '/property': 'Property',
  '/source': 'Source Deals',
  '/deal': 'Deal',
  '/deal/new': 'New Deal',
};

export function Breadcrumbs({ items, className = '' }: BreadcrumbsProps) {
  const location = useLocation();

  // Generate breadcrumbs from route if items not provided
  const crumbs = React.useMemo(() => {
    if (items) {
      return items;
    }

    const segments = location.pathname.split('/').filter(Boolean);
    const breadcrumbs: BreadcrumbItem[] = [
      { label: 'Home', path: '/' }
    ];

    if (segments.length === 0) {
      // We're on home page
      return [{ label: 'Home' }];
    }

    let accumulatedPath = '';
    segments.forEach((segment, index) => {
      accumulatedPath += `/${segment}`;
      const isLast = index === segments.length - 1;

      // Look up label in ROUTE_LABELS, fallback to segment with # prefix if numeric
      const label = ROUTE_LABELS[accumulatedPath] ||
                    (isNaN(Number(segment)) ? segment : `#${segment}`);

      breadcrumbs.push({
        label,
        path: isLast ? undefined : accumulatedPath
      });
    });

    return breadcrumbs;
  }, [items, location.pathname]);

  return (
    <nav
      aria-label="Breadcrumb"
      className={`flex items-center gap-2 text-xs sm:text-sm overflow-x-auto flex-nowrap ${className}`}
      style={{ scrollbarWidth: 'none' }}
    >
      {crumbs.map((crumb, index) => (
        <React.Fragment key={crumb.label + index}>
          {index > 0 && (
            <ChevronRight className="w-3.5 h-3.5 text-stone-300 flex-shrink-0" />
          )}
          {crumb.path ? (
            <Link
              to={crumb.path}
              className="text-stone-500 hover:text-gold transition-colors flex items-center gap-1.5"
            >
              {index === 0 && <Home className="w-3.5 h-3.5" />}
              <span className="truncate max-w-[120px] sm:max-w-none">
                {crumb.label}
              </span>
            </Link>
          ) : (
            <span className="text-ivory font-medium flex items-center gap-1.5">
              {index === 0 && <Home className="w-3.5 h-3.5" />}
              <span className="truncate max-w-[120px] sm:max-w-none">
                {crumb.label}
              </span>
            </span>
          )}
        </React.Fragment>
      ))}
    </nav>
  );
}
